import React, { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { canMakeSales } from "../utils/roles";
import { all_routes as routes } from "./all_routes";

// Feature pages (lazy-loaded so the initial bundle only ships the current route)
const Customers = lazy(() => import("../feature-module/people/customers"));
const Appointments = lazy(() => import("../feature-module/appointments/appointments"));
const PromoCodes = lazy(() => import("../feature-module/people/promocodes"));
const SmsDashboard = lazy(() => import("../feature-module/sms/smsDashboard"));
const SmsTemplates = lazy(() => import("../feature-module/sms/smsTemplates"));
const SmsManualSend = lazy(() => import("../feature-module/sms/smsManualSend"));
const SmsLogs = lazy(() => import("../feature-module/sms/smsLogs"));
const WhatsappInbox = lazy(() => import("../feature-module/whatsapp/whatsappInbox"));
const Profile = lazy(() => import("../feature-module/pages/profile"));
const Signin = lazy(() => import("../feature-module/pages/login/signin"));
const Error404 = lazy(() => import("../feature-module/pages/errorpages/error404"));
const Error500 = lazy(() => import("../feature-module/pages/errorpages/error500"));
const Blankpage = lazy(() => import("../feature-module/pages/blankpage"));
const Comingsoon = lazy(() => import("../feature-module/pages/comingsoon"));
const Undermaintainence = lazy(() => import("../feature-module/pages/undermaintainence"));
const Users = lazy(() => import("../feature-module/usermanagement/users"));
const Pos2 = lazy(() => import("../feature-module/pos/pos2"));
const CustomerDisplay = lazy(() => import("../feature-module/pos/customerDisplay"));
const Invoice = lazy(() => import("../feature-module/sales/invoicereport"));
const Invoicedetails = lazy(() => import("../feature-module/sales/invoicedetails"));
const Designation = lazy(() => import("../feature-module/hrm/designation"));
const EmployeesGrid = lazy(() => import("../feature-module/hrm/employeesgrid"));
const EditEmployee = lazy(() => import("../feature-module/hrm/editemployee"));
const AddEmployee = lazy(() => import("../feature-module/hrm/addemployee"));
const LeavesEmployee = lazy(() => import("../feature-module/hrm/leavesemployee"));
const PayrollList = lazy(() => import("../feature-module/hrm/payroll-list"));
const Employeedetails = lazy(() => import("../feature-module/hrm/employeedetails"));
const PrinterSettings = lazy(() => import("../feature-module/settings/appsetting/printersettings"));
const DiscountSettings = lazy(() => import("../feature-module/settings/appsetting/discountsettings"));
const UserManual = lazy(() => import("../feature-module/settings/appsetting/usermanual"));
const ExpensesList = lazy(() => import("../feature-module/FinanceAccounts/expenseslist"));
const GiftVouchers = lazy(() => import("../feature-module/FinanceAccounts/giftVouchers"));
const CategoryList = lazy(() => import("../feature-module/inventory/categorylist"));
const EditProduct = lazy(() => import("../feature-module/inventory/editproduct"));
const ProductDetail = lazy(() => import("../feature-module/inventory/productdetail"));
const ProductList = lazy(() => import("../feature-module/inventory/productlist"));
const AddProduct = lazy(() => import("../feature-module/inventory/addproduct"));
const Managestock = lazy(() => import("../feature-module/stock/managestock"));
const StockTransfer = lazy(() => import("../feature-module/stock/stockTransfer"));
const SalesDashboard = lazy(() => import("../feature-module/dashboard/salesdashboard"));
const SalesReport = lazy(() => import("../feature-module/Reports/salesreport"));
const CustomerReport = lazy(() => import("../feature-module/Reports/customerreport"));
const ExpenseReport = lazy(() => import("../feature-module/Reports/expensereport"));
const BestSeller = lazy(() => import("../feature-module/Reports/bestseller"));
const EmployeeEventReport = lazy(() => import("../feature-module/Reports/employeeeventreport"));
const EmployeeServiceReport = lazy(() => import("../feature-module/Reports/employeeservicereport"));

// ------------------------
// Base route groups
// ------------------------
const basePublicRoutes = [
  { id: 84, path: routes.customers, name: "customers", element: <Customers /> },
  { id: 85, path: routes.appointments, name: "appointments", element: <Appointments /> },
  { id: 106, path: routes.profile, name: "profile", element: <Profile /> },
  {
    id: 75,
    path: routes.printersettings,
    name: "printersettings",
    element: <PrinterSettings />,
    route: Route,
  },
  { id: 20, path: routes.blankpage, name: "blankpage", element: <Blankpage /> },
  { id: 117, path: "/", name: "Root", element: <Navigate to="/signin" /> },
  { id: 116, path: "*", name: "NotFound", element: <Navigate to="/" /> },
].map(r => ({ ...r, route: Route }));

const hrRoutes = [
  { id: 108, path: routes.employeegrid, name: "employeegrid", element: <EmployeesGrid /> },
  { id: 109, path: routes.addemployee, name: "addemployee", element: <AddEmployee /> },
  { id: 110, path: routes.editemployee, name: "editemployee", element: <EditEmployee /> },
  { id: 112, path: routes.leavesemployee, name: "leavesemployee", element: <LeavesEmployee /> },
  { id: 40, path: routes.designation, name: "designation", element: <Designation /> },
  { id: 119, path: routes.payrollList, name: "payroll-list", element: <PayrollList /> },
  { id: 131, path: routes.employeedetails, name: "billers", element: <Employeedetails /> }
].map(r => ({ ...r, route: Route }));

const invoiceRoutes = [
  { id: 122, path: routes.invoice, name: "invoice", element: <Invoice /> },
  { id: 123, path: routes.invoicedetails, name: "invoice-details", element: <Invoicedetails /> },
  {
    id: 61,
    path: routes.expenselist,
    name: "expenselist",
    element: <ExpensesList />,
    route: Route,
  },
].map(r => ({ ...r, route: Route }));

const promoCodeRoutes = [
  {id: 200, path: routes.promocodes, name: "promo-codes", element: <PromoCodes />},
  {id: 201, path: routes.giftvouchers, name: "gift-vouchers", element: <GiftVouchers />},
].map(r => ({...r, route: Route}));

const smsRoutes = [
  {id: 210, path: routes.smsdashboard, name: "sms-dashboard", element: <SmsDashboard />},
  {id: 211, path: routes.smstemplates, name: "sms-templates", element: <SmsTemplates />},
  {id: 212, path: routes.smssend, name: "sms-send", element: <SmsManualSend />},
  {id: 213, path: routes.smslogs, name: "sms-logs", element: <SmsLogs />},
].map(r => ({...r, route: Route}));

const whatsappRoutes = [
  {id: 214, path: routes.whatsappinbox, name: "whatsapp-inbox", element: <WhatsappInbox />},
].map(r => ({...r, route: Route}));

const discountSettingsRoutes = [{id: 221, path: routes.discountsettings, name: "discount-settings", element: <DiscountSettings />, route: Route}];

const adminRoutes = [
  { id: 104, path: routes.users, name: "users", element: <Users /> }
].map(r => ({ ...r, route: Route }));

const userManualRoutes = [
  { id: 220, path: routes.usermanual, name: "usermanual", element: <UserManual /> }
].map(r => ({ ...r, route: Route }));

const posRoutesBase = [
  { id: 1, path: routes.pos, name: "pos", element: <Pos2 /> }
].map(r => ({ ...r, route: Route }));

const inventoryRoutes = [
  {
    id: 2,
    path: routes.productlist,
    name: "products",
    element: <ProductList />,
    route: Route,
  },
  {
    id: 3,
    path: routes.addproduct,
    name: "products",
    element: <AddProduct />,
    route: Route,
  },
  {
    id: 60,
    path: routes.categorylist,
    name: "categorylist",
    element: <CategoryList />,
    route: Route,
  },
  {
    id: 65,
    path: routes.editproduct,
    name: "editproduct",
    element: <EditProduct />,
    route: Route,
  },
  {
    id: 113,
    path: routes.productdetails,
    name: "productdetails",
    element: <ProductDetail />,
    route: Route,
  },
].map(r => ({ ...r, route: Route }));

const stockRoutes = [
    {
    id: 87,
    path: routes.managestock,
    name: "managestock",
    element: <Managestock />,
    route: Route,
  },
  {
    id: 89,
    path: routes.stocktransfer,
    name: "stocktransfer",
    element: <StockTransfer />,
    route: Route,
  },
].map(r => ({ ...r, route: Route }));

const dashboardRoutes = [
  { id: 4, path: routes.salesdashboard, name: "salesdashboard", element: <SalesDashboard />, route: Route }
].map(r => ({ ...r, route: Route }));

const reportRoutes = [
  {
    id: 90,
    path: routes.salesreport,
    name: "salesreport",
    element: <SalesReport />,
    route: Route,
  },
  {
    id: 140,
    path: routes.employeeeventreport,
    name: "employeeeventreport",
    element: <EmployeeEventReport />,
    route: Route,
  },
    {
    id: 134,
    path: routes.bestseller,
    name: "best-sellers",
    element: <BestSeller />,
    route: Route,
  },
  {
    id: 95,
    path: routes.customerreport,
    name: "customerreport",
    element: <CustomerReport />,
    route: Route,
  },
  {
    id: 96,
    path: routes.expensereport,
    name: "expensereport",
    element: <ExpenseReport />,
    route: Route,
  },
    {
    id: 150,
    path: routes.employeeservicereport,
    name: "employeeservicereport",
    element: <EmployeeServiceReport />,
    route: Route,
  },
].map(r => ({ ...r, route: Route }));

const pagesRoute = [
  { id: 1, path: routes.signin, name: "signin", element: <Signin /> },
  { id: 22, path: routes.customerdisplay, name: "customerdisplay", element: <CustomerDisplay /> },
  { id: 18, path: routes.error404, name: "error404", element: <Error404 /> },
  { id: 19, path: routes.error500, name: "error500", element: <Error500 /> },
  { id: 20, path: routes.comingsoon, name: "comingsoon", element: <Comingsoon /> },
  { id: 21, path: routes.undermaintenance, name: "undermaintenance", element: <Undermaintainence /> }
].map(r => ({ ...r, route: Route }));

// ------------------------
// Hook
// ------------------------
export const getRouteGroups = (role, whatsappInboxAccess = false) => {
  const roleMap = {
    su: [...discountSettingsRoutes, ...adminRoutes, ...userManualRoutes, ...promoCodeRoutes, ...smsRoutes, ...whatsappRoutes, ...invoiceRoutes, ...hrRoutes, ...inventoryRoutes, ...stockRoutes, ...dashboardRoutes, ...reportRoutes],
    admin: [...discountSettingsRoutes, ...adminRoutes, ...promoCodeRoutes, ...smsRoutes, ...whatsappRoutes, ...invoiceRoutes, ...hrRoutes, ...inventoryRoutes, ...stockRoutes, ...dashboardRoutes, ...reportRoutes],
    compta: [...invoiceRoutes, ...hrRoutes, ...dashboardRoutes, ...reportRoutes],
    manager: [...invoiceRoutes, ...(hrRoutes[3] ? [hrRoutes[3]] : []), ...inventoryRoutes, ...stockRoutes, ...smsRoutes, ...whatsappRoutes],
    logistic: [...inventoryRoutes, ...stockRoutes, ...reportRoutes.filter(report => report.path === routes.bestseller)],
    rh: [...hrRoutes, ...reportRoutes, ...invoiceRoutes],
    salon_manager: [...invoiceRoutes],
    com: [...smsRoutes, ...whatsappRoutes],
  };

  const roleHasWhatsappInboxRoutes = ["su", "admin", "manager", "com"].includes(role);

  const publicRoutes = [
    ...basePublicRoutes,
    ...(roleMap[role] || []),
    ...(!roleHasWhatsappInboxRoutes && whatsappInboxAccess ? whatsappRoutes : []),
  ];

  const posRoutes = canMakeSales(role) ? [...posRoutesBase] : [];

  return {
    publicRoutes,
    posRoutes,
    pagesRoute
  };
};

// Navigation and routing share the same access rules.
export const usePublicRoutes = () => {
  const role = useSelector((state) => state.rootReducer.user_infos?.role);
  const whatsappInboxAccess = useSelector((state) => state.rootReducer.user_infos?.whatsappInboxAccess);
  return getRouteGroups(role, whatsappInboxAccess);
};
